import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { ArrowLeft, X, RefreshCw, Copy } from 'lucide-react';

const STATUS_FLOW = ['Recebido', 'Em Produção', 'Pronto', 'Em Entrega', 'Entregue'];

export default function PedidoDetalheMobile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [error, setError] = useState('');

  const fetchPedido = useCallback(async () => {
    try {
      const res = await api.get(`/pedidos/${id}`);
      setPedido(res.data);
    } catch {
      navigate('/pedidos/historico');
    }
  }, [id, navigate]);

  useEffect(() => {
    setLoading(true);
    fetchPedido().finally(() => setLoading(false));
  }, [fetchPedido]);

  // Polling a cada 60s para atualizar status
  useEffect(() => {
    const interval = setInterval(fetchPedido, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPedido]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPedido();
    setRefreshing(false);
  };

  const handleCancel = async () => {
    if (!motivo.trim()) { setError('Informe o motivo do cancelamento'); return; }
    setCancelling(true);
    try {
      await api.put(`/pedidos/${id}/status`, { status: 'Cancelado', motivo });
      setPedido(p => ({ ...p, status: 'Cancelado' }));
      setShowCancel(false);
    } catch (err) {
      setError(err.userMessage || 'Erro ao cancelar');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!pedido) return null;

  const stepIndex = STATUS_FLOW.indexOf(pedido.status);

  return (
    <div className="pb-8" data-testid="pedido-detalhe-mobile">
      {/* Header */}
      <div className="bg-brand-primary px-4 pt-10 pb-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white opacity-80">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-body">Voltar</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-body transition-colors"
            data-testid="refresh-pedido-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Atualizando...' : 'Atualizar'}</span>
          </button>
        </div>
        <h1 className="font-heading text-2xl font-bold">Pedido #{pedido.numero}</h1>
        <p className="text-sm opacity-80 font-body mt-1">{formatDate(pedido.data_pedido)} · Prazo: {pedido.prazo}</p>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Status */}
        <div className="card-mobile">
          <div className="flex items-center justify-between mb-4">
            <p className="font-dashboard font-semibold text-brand-text text-sm">Status</p>
            <StatusBadge status={pedido.status} />
          </div>
          {pedido.status !== 'Cancelado' && (
            <div className="flex items-center gap-1">
              {STATUS_FLOW.map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i <= stepIndex ? 'bg-brand-primary text-white' : 'bg-brand-border text-brand-text-muted'}`}>
                    {i < stepIndex ? '✓' : i + 1}
                  </div>
                  {i < STATUS_FLOW.length - 1 && <div className={`flex-1 h-0.5 ${i < stepIndex ? 'bg-brand-primary' : 'bg-brand-border'}`} />}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Items */}
        <div className="card-mobile">
          <p className="font-dashboard font-semibold text-brand-text text-sm mb-3">Itens do Pedido</p>
          <div className="space-y-2">
            {pedido.itens?.map((item, i) => (
              <div key={i} className="flex items-start gap-2 py-2 border-b border-brand-border last:border-0">
                <div className="flex-1">
                  <p className="text-sm font-body text-brand-text">{item.descricao}</p>
                  <p className="text-xs text-brand-text-muted">{item.sku} · {item.unidade}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold font-body text-brand-text">{item.quantidade}x</p>
                  {item.preco_total != null && <p className="text-xs text-brand-primary">{formatCurrency(item.preco_total)}</p>}
                </div>
              </div>
            ))}
          </div>
          {pedido.total != null && (
            <div className="flex justify-between pt-3 mt-2 border-t border-brand-border">
              <span className="font-dashboard font-bold text-sm text-brand-text">Total</span>
              <span className="font-dashboard font-bold text-sm text-brand-primary">{formatCurrency(pedido.total)}</span>
            </div>
          )}
        </div>

        {/* Forma de pagamento */}
        {pedido.forma_pagamento && (
          <div className="card-mobile" data-testid="pedido-pagamento">
            <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-2">Pagamento</p>
            <p className="text-sm font-body font-medium text-brand-text">{pedido.forma_pagamento}</p>
            {pedido.chave_pix_usada && (
              <div className="mt-3 bg-brand-bg rounded-xl p-3">
                <p className="text-xs text-brand-text-muted font-body mb-1">Chave Pix</p>
                <p className="text-sm font-dashboard font-bold text-brand-text break-all">{pedido.chave_pix_usada}</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(pedido.chave_pix_usada); }}
                  className="mt-2 flex items-center gap-1.5 text-xs text-brand-primary font-body font-medium"
                  data-testid="copiar-pix-detalhe-btn"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar Chave Pix
                </button>
              </div>
            )}
          </div>
        )}

        {/* Observações */}
        {pedido.observacoes && (
          <div className="card-mobile" data-testid="pedido-observacoes">
            <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-1">Observações</p>
            <p className="text-sm font-body text-brand-text">{pedido.observacoes}</p>
          </div>
        )}

        {/* Cancel */}
        {pedido.status === 'Recebido' && (
          <button onClick={() => setShowCancel(true)} className="w-full py-3 rounded-xl border-2 border-red-200 text-red-600 font-dashboard font-medium text-sm hover:bg-red-50" data-testid="cancel-order-btn">
            Cancelar Pedido
          </button>
        )}

        {/* Clonar */}
        <button
          onClick={() => navigate(`/pedidos?base=${id}`)}
          className="w-full py-3 rounded-xl border-2 border-brand-border text-brand-text font-dashboard font-medium text-sm flex items-center justify-center gap-2 hover:bg-brand-bg"
          data-testid="clonar-pedido-btn"
        >
          <Copy className="w-4 h-4" />
          Clonar Pedido
        </button>

        {/* Cancel modal */}
        {showCancel && (
          <div className="fixed inset-0 bg-black/50 flex items-end z-50 p-4">
            <div className="w-full max-w-sm mx-auto bg-white rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-dashboard font-bold text-brand-text">Cancelar Pedido</h3>
                <button onClick={() => setShowCancel(false)}><X className="w-5 h-5 text-brand-text-muted" /></button>
              </div>
              {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
              <textarea
                className="w-full rounded-xl border border-brand-border p-3 text-sm font-body h-24 resize-none"
                placeholder="Motivo do cancelamento (obrigatório)"
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                data-testid="cancel-motivo"
              />
              <button onClick={handleCancel} disabled={cancelling} className="w-full mt-3 py-3 rounded-xl bg-red-600 text-white font-dashboard font-semibold text-sm disabled:opacity-60" data-testid="confirm-cancel-btn">
                {cancelling ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
